import classnames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import {
  IconButton,
  SuggestInput,
  TextInput,
  CloseIcon as closeIcon,
  GreenCheckIcon as greenCheckIcon,
  NewTabIconGreen as newTabIcon,
} from '@/shared/ui';
import { SUGGEST_COMMENT, TYPE_EVENT_OTHER } from '@/shared/config';
import useEditStripBox from './useEditStripBox';

export type EditStripBoxProps = {
  className?: string;
  isActive?: boolean;
};

/**
 * Detail pane for the selected AI-captured strip, showing its identifying fields and the editable title and technician comment. useEditStripBox drives it: a skeleton covers strip loading and a no-data message shows when nothing is selected, event id, type, trigger, captured time and symptoms appear only for the event kinds that carry them, and per-field cancel and apply buttons fade in once a value diverges from the stored one, locking while the event is removed or a save is in flight.
 *
 * @param props - Pane props including the extra className and the active flag passed to the hook.
 * @returns The rendered detail pane, its loading skeleton, or the no-data message.
 */
const EditStripBox = ({ className, isActive }: EditStripBoxProps) => {
  const {
    selectedData,
    isDisabled,
    isLoadingStrip,
    isSavingStripManagement,
    state,
    eventId,
    eventType,
    triggerTime,
    symptoms,
    capturedTime,
    onClickNavigate,
    onChange,
    onKeyDownInput,
    onClickApplyChange,
    onClickCancelChange,
  } = useEditStripBox({ isActive });

  return (
    <div className={classnames('edit-strip-box', className)}>
      {isLoadingStrip ? (
        <div className="edit-strip-box-skeleton">
          <Skeleton height="100%" />
        </div>
      ) : selectedData.id ? (
        <>
          <div className="edit-strip-top-content">
            {state.isDeviceEvent && (
              <div className="edit-strip-top-content__row">
                <div className="top-row-title --auto-event">Event ID:</div>
                <div className="top-row-value">{eventId}</div>
                <IconButton
                  id="auto-event-open-event-btn"
                  iconComponent={<img src={newTabIcon} alt="Close icon" />}
                  onClick={onClickNavigate}
                  iconText="Open"
                  isOpposite
                  isIconWithText
                  className="top-row-button"
                />
              </div>
            )}
            {!selectedData.isShowSymptoms && !state.isOtherEvent && (
              <div className="edit-strip-top-content__row">
                <div className="top-row-title --auto-event">Event type:</div>
                <div className="top-row-value">{eventType}</div>
              </div>
            )}
            <div className="edit-strip-top-content__row">
              <div className="top-row-title --auto-event">Trigger time:</div>
              <div className="top-row-value">{triggerTime}</div>
            </div>
            {!state.isDeviceEvent && (
              <div className="edit-strip-top-content__row">
                <div className="top-row-title --auto-event">Captured time:</div>
                <div className="top-row-value">{capturedTime}</div>
              </div>
            )}
            {state.isDeviceEvent && selectedData.isShowSymptoms && (
              <div className="edit-strip-top-content__row">
                <div className="top-row-title --auto-event">Symptoms:</div>
                <div className="top-row-value">{symptoms || 'NA'}</div>
              </div>
            )}
            {state.type === TYPE_EVENT_OTHER && (
              <div className="edit-strip-top-content__row">
                <div className="top-row-title title-with-input">Title:</div>
                <TextInput
                  className="input-title-item"
                  name="title"
                  label=""
                  disabled={state.isRemoved || isSavingStripManagement}
                  value={state.title}
                  onChange={onChange}
                  isError={!!state.titleError}
                  errorMessage={state.titleError}
                  placeholder="Enter a title"
                  onKeyDown={onKeyDownInput}
                  maxLength={100}
                  id="edit-strip-box-title-input"
                />
                {selectedData?.title !== state.title && !isDisabled && (
                  <div className="btm-btn-container fadeIn animated faster">
                    <IconButton
                      id="strips-ai-cancel-title-btn"
                      isOutline
                      iconComponent={<img src={closeIcon} alt="Close icon" />}
                      onClick={() => {
                        onClickCancelChange('title');
                      }}
                    />
                    <IconButton
                      id="strips-ai-apply-title-btn"
                      isFilled
                      iconComponent={<img src={greenCheckIcon} alt="Close icon" />}
                      onClick={() => {
                        onClickApplyChange('title');
                      }}
                      disabled={state.isRemoved || isSavingStripManagement}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="divider" />
          <div className="edit-strip-bottom-content">
            <div className="edit-strip-bottom-content__row">
              <div className="btm-row-title">Technician comment:</div>
              {selectedData?.comment !== state.technicianComment && !isDisabled && (
                <div className="btm-btn-container fadeIn animated faster">
                  <IconButton
                    id="strips-ai-cancel-comment-btn"
                    isOutline
                    iconComponent={<img src={closeIcon} alt="Close icon" />}
                    onClick={onClickCancelChange}
                  />
                  <IconButton
                    id="strips-ai-apply-comment-btn"
                    isFilled
                    iconComponent={<img src={greenCheckIcon} alt="Close icon" />}
                    onClick={onClickApplyChange}
                    disabled={state.isRemoved || isSavingStripManagement}
                  />
                </div>
              )}
            </div>
            <SuggestInput
              className="leave-comment-textarea"
              name="technicianComment"
              type="textarea"
              label=""
              disabled={state.isRemoved || isSavingStripManagement}
              value={state.technicianComment}
              onChange={onChange}
              isError={!!state.commentError}
              errorMessage={state.commentError}
              placeholder="Enter comment..."
              onKeyDown={onKeyDownInput}
              maxLength={520}
              id="edit-strip-box-technician-comment-input"
              suggestTexts={SUGGEST_COMMENT}
              isReverse
            />
          </div>
        </>
      ) : (
        <div className="no-data-display-container">There is no data to display</div>
      )}
    </div>
  );
};

export default EditStripBox;
